'use client';
import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────
type ChallengeCategory = 'prayer' | 'charity' | 'helping' | 'quran' | 'fasting' | 'dhikr' | 'masjid' | 'general';
type ChallengeStatus = 'active' | 'completed' | 'cancelled';
type ActiveTab = 'browse' | 'my-challenges' | 'create';

interface Challenge {
  id: string;
  creator_id: string;
  title: string;
  description: string | null;
  category: ChallengeCategory;
  emoji: string;
  target_count: number;
  unit: string;
  duration_days: number;
  start_date: string;
  end_date: string;
  status: ChallengeStatus;
  is_public: boolean;
  max_participants: number;
  created_at: string;
  participant_count?: number;
  my_progress?: number;
  is_joined?: boolean;
  creator_name?: string;
}

interface LeaderEntry {
  user_id: string;
  display_name: string;
  current_count: number;
  target_count: number;
  avatar_initials: string;
}

const CATEGORIES: { value: ChallengeCategory; label: string; emoji: string; color: string }[] = [
  { value: 'masjid', label: 'Masjid Prayer', emoji: '🕌', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200' },
  { value: 'charity', label: 'Charity', emoji: '💝', color: 'bg-rose-500/10 text-rose-600 border-rose-200' },
  { value: 'helping', label: 'Helping Others', emoji: '🤝', color: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  { value: 'prayer', label: 'Prayer', emoji: '🙏', color: 'bg-teal-500/10 text-teal-600 border-teal-200' },
  { value: 'quran', label: 'Quran', emoji: '📖', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  { value: 'fasting', label: 'Fasting', emoji: '🌙', color: 'bg-indigo-500/10 text-indigo-600 border-indigo-200' },
  { value: 'dhikr', label: 'Dhikr', emoji: '📿', color: 'bg-purple-500/10 text-purple-600 border-purple-200' },
  { value: 'general', label: 'General', emoji: '🎯', color: 'bg-slate-500/10 text-slate-600 border-slate-200' },
];

const getCategoryStyle = (cat: ChallengeCategory) =>
  CATEGORIES.find((c) => c.value === cat) ?? CATEGORIES[CATEGORIES.length - 1];

function daysLeft(endDate: string): number {
  return Math.max(0, Math.ceil((new Date(endDate).getTime() - Date.now()) / 86400000));
}

function progressPct(current: number, target: number): number {
  return Math.min(100, Math.round((current / Math.max(1, target)) * 100));
}

// ─── Leaderboard Modal ────────────────────────────────────────────────────────
function LeaderboardModal({
  challenge,
  onClose,
}: {
  challenge: Challenge;
  onClose: () => void;
}) {
  const [leaders, setLeaders] = useState<LeaderEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('challenge_participants')
        .select('user_id, current_count, user_profiles(full_name, email)')
        .eq('challenge_id', challenge.id)
        .order('current_count', { ascending: false })
        .limit(20);

      if (data) {
        setLeaders(
          data.map((row: any) => ({
            user_id: row.user_id,
            display_name: row.user_profiles?.full_name || row.user_profiles?.email?.split('@')[0] || 'Anonymous',
            current_count: row.current_count ?? 0,
            target_count: challenge.target_count,
            avatar_initials: (row.user_profiles?.full_name || 'AN')
              .split(' ')
              .map((n: string) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2),
          }))
        );
      }
      setLoading(false);
    }
    load();
  }, [challenge.id]);

  const medalColors = ['text-yellow-500', 'text-slate-400', 'text-amber-600'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md border border-border">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{challenge.emoji}</span>
            <div>
              <h3 className="font-700 text-foreground text-sm leading-tight">{challenge.title}</h3>
              <p className="text-xs text-muted-foreground">Leaderboard</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 max-h-96 overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : leaders.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-8">No participants yet. Be the first!</p>
          ) : (
            leaders.map((entry, idx) => (
              <div key={entry.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/40">
                <span className={`text-lg font-800 w-6 text-center ${medalColors[idx] ?? 'text-muted-foreground'}`}>
                  {idx < 3 ? ['🥇', '🥈', '🥉'][idx] : `${idx + 1}`}
                </span>
                <div className="w-8 h-8 rounded-full gradient-teal flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
                  {entry.avatar_initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-600 text-foreground truncate">{entry.display_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${progressPct(entry.current_count, entry.target_count)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {entry.current_count}/{entry.target_count}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Challenge Card ───────────────────────────────────────────────────────────
function ChallengeCard({
  challenge,
  onJoin,
  onLog,
  onLeaderboard,
  joining,
  logging,
}: {
  challenge: Challenge;
  onJoin: (id: string) => void;
  onLog: (id: string) => void;
  onLeaderboard: (c: Challenge) => void;
  joining: string | null;
  logging: string | null;
}) {
  const catStyle = getCategoryStyle(challenge.category);
  const pct = progressPct(challenge.my_progress ?? 0, challenge.target_count);
  const left = daysLeft(challenge.end_date);

  return (
    <div className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4 hover:shadow-card transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{challenge.emoji}</span>
          <div>
            <h3 className="font-700 text-foreground text-sm leading-snug">{challenge.title}</h3>
            <span className={`inline-flex items-center gap-1 text-xs font-500 px-2 py-0.5 rounded-full border mt-1 ${catStyle.color}`}>
              {catStyle.emoji} {catStyle.label}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${left <= 1 ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'}`}>
            {left === 0 ? 'Ends today' : `${left}d left`}
          </span>
        </div>
      </div>

      {/* Description */}
      {challenge.description && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{challenge.description}</p>
      )}

      {/* Progress (if joined) */}
      {challenge.is_joined && (
        <div>
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span>Your progress</span>
            <span className="font-600 text-foreground">{challenge.my_progress ?? 0}/{challenge.target_count} {challenge.unit}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">{pct}% complete</p>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => onLeaderboard(challenge)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="TrophyIcon" size={14} />
          <span>{challenge.participant_count ?? 0} competing</span>
        </button>
        <div className="flex items-center gap-2">
          {challenge.is_joined ? (
            <button
              onClick={() => onLog(challenge.id)}
              disabled={logging === challenge.id}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-600 rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {logging === challenge.id ? (
                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="PlusCircleIcon" size={14} />
              )}
              Log +1
            </button>
          ) : (
            <button
              onClick={() => onJoin(challenge.id)}
              disabled={joining === challenge.id}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary text-xs font-600 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-60 border border-primary/20"
            >
              {joining === challenge.id ? (
                <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="UserPlusIcon" size={14} />
              )}
              Join
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Create Form ──────────────────────────────────────────────────────────────
function CreateChallengeForm({ onCreated }: { onCreated: () => void }) {
  const { user } = useAuth();
  const supabase = createClient();
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'general' as ChallengeCategory,
    emoji: '🏆',
    target_count: 7,
    unit: 'times',
    duration_days: 7,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const EMOJI_OPTIONS = ['🏆', '🕌', '💝', '🤝', '📖', '🌙', '📿', '🎯', '💪', '⭐'];

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    setError(null);
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + form.duration_days);

    const { error: err } = await supabase.from('challenges').insert({
      creator_id: user.id,
      title: form.title.trim(),
      description: form.description.trim() || null,
      category: form.category,
      emoji: form.emoji,
      target_count: form.target_count,
      unit: form.unit.trim() || 'times',
      duration_days: form.duration_days,
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      status: 'active',
      is_public: true,
    });

    setSaving(false);
    if (err) {
      setError(err.message);
    } else {
      setForm({ title: '', description: '', category: 'general', emoji: '🏆', target_count: 7, unit: 'times', duration_days: 7 });
      onCreated();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 space-y-5 max-w-xl">
      <h3 className="font-700 text-foreground text-base">Create a New Challenge</h3>

      {/* Emoji picker */}
      <div>
        <label className="block text-xs font-600 text-muted-foreground mb-2">Challenge Icon</label>
        <div className="flex flex-wrap gap-2">
          {EMOJI_OPTIONS.map((em) => (
            <button
              key={em}
              type="button"
              onClick={() => setForm((f) => ({ ...f, emoji: em }))}
              className={`w-9 h-9 rounded-xl text-lg flex items-center justify-center border-2 transition-colors ${form.emoji === em ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40'}`}
            >
              {em}
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-xs font-600 text-muted-foreground mb-1.5">Challenge Title *</label>
        <input
          required
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Pray every Salah in the Masjid"
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-600 text-muted-foreground mb-1.5">Description</label>
        <textarea
          rows={3}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Describe the challenge and its spiritual significance..."
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-xs font-600 text-muted-foreground mb-1.5">Category</label>
        <div className="grid grid-cols-4 gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setForm((f) => ({ ...f, category: cat.value }))}
              className={`flex flex-col items-center gap-1 p-2 rounded-xl border text-xs font-500 transition-colors ${form.category === cat.value ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-primary/40 text-muted-foreground'}`}
            >
              <span className="text-base">{cat.emoji}</span>
              <span className="leading-tight text-center">{cat.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Target & Unit */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1.5">Target Count</label>
          <input
            type="number"
            min={1}
            max={1000}
            value={form.target_count}
            onChange={(e) => setForm((f) => ({ ...f, target_count: parseInt(e.target.value) || 1 }))}
            className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1.5">Unit</label>
          <input
            value={form.unit}
            onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
            placeholder="prayers, acts, pages..."
            className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className="block text-xs font-600 text-muted-foreground mb-1.5">Duration: {form.duration_days} days</label>
        <input
          type="range"
          min={1}
          max={30}
          value={form.duration_days}
          onChange={(e) => setForm((f) => ({ ...f, duration_days: parseInt(e.target.value) }))}
          className="w-full accent-primary"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>1 day</span>
          <span>30 days</span>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
      )}

      <button
        type="submit"
        disabled={saving || !form.title.trim()}
        className="w-full py-3 bg-primary text-primary-foreground font-600 text-sm rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {saving ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Icon name="PlusCircleIcon" size={16} />
        )}
        {saving ? 'Creating...' : 'Create Challenge'}
      </button>
    </form>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ChallengesPage() {
  const { user } = useAuth();
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<ActiveTab>('browse');
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [myChallenges, setMyChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState<string | null>(null);
  const [logging, setLogging] = useState<string | null>(null);
  const [leaderboardChallenge, setLeaderboardChallenge] = useState<Challenge | null>(null);
  const [filterCat, setFilterCat] = useState<ChallengeCategory | 'all'>('all');
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const loadChallenges = useCallback(async () => {
    setLoading(true);
    const { data: allData } = await supabase
      .from('challenges')
      .select('*, challenge_participants(user_id, current_count)')
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (allData) {
      const enriched: Challenge[] = allData.map((c: any) => {
        const participants: any[] = c.challenge_participants ?? [];
        const myPart = user ? participants.find((p) => p.user_id === user.id) : null;
        return {
          ...c,
          participant_count: participants.length,
          my_progress: myPart?.current_count ?? 0,
          is_joined: !!myPart,
          challenge_participants: undefined,
        };
      });
      setChallenges(enriched);
      if (user) {
        setMyChallenges(enriched.filter((c) => c.is_joined || c.creator_id === user.id));
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  async function handleJoin(challengeId: string) {
    if (!user) return;
    setJoining(challengeId);
    const { error } = await supabase.from('challenge_participants').insert({
      challenge_id: challengeId,
      user_id: user.id,
      status: 'joined',
      current_count: 0,
    });
    setJoining(null);
    if (!error) {
      showToast('Joined challenge! May Allah make it easy for you. 🤲');
      loadChallenges();
    }
  }

  async function handleLog(challengeId: string) {
    if (!user) return;
    setLogging(challengeId);
    const { error: logErr } = await supabase.from('challenge_logs').insert({
      challenge_id: challengeId,
      user_id: user.id,
      count: 1,
    });
    if (!logErr) {
      const challenge = challenges.find((c) => c.id === challengeId);
      const newCount = (challenge?.my_progress ?? 0) + 1;
      await supabase
        .from('challenge_participants')
        .update({ current_count: newCount })
        .eq('challenge_id', challengeId)
        .eq('user_id', user.id);
      showToast('Progress logged! Keep going! ⭐');
      loadChallenges();
    }
    setLogging(null);
  }

  const displayedChallenges = (activeTab === 'my-challenges' ? myChallenges : challenges).filter(
    (c) => filterCat === 'all' || c.category === filterCat
  );

  const tabs: { id: ActiveTab; label: string; icon: string }[] = [
    { id: 'browse', label: 'Browse', icon: 'MagnifyingGlassIcon' },
    { id: 'my-challenges', label: 'My Challenges', icon: 'TrophyIcon' },
    { id: 'create', label: 'Create', icon: 'PlusCircleIcon' },
  ];

  return (
    <AppLayout activeRoute="/challenges">
      <div className="flex-1 overflow-y-auto bg-background">
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-800 text-foreground">Community Challenges</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Compete with your brothers and sisters in good deeds. Race towards Allah&apos;s pleasure.
              </p>
            </div>
            <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-2 rounded-xl border border-primary/20">
              <span className="text-lg">🏁</span>
              <span className="text-xs font-600">{challenges.length} Active</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-muted/50 p-1 rounded-xl w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 ${
                  activeTab === tab.id
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon as any} size={15} />
                {tab.label}
                {tab.id === 'my-challenges' && myChallenges.length > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {myChallenges.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Create Tab */}
          {activeTab === 'create' && (
            <CreateChallengeForm
              onCreated={() => {
                showToast('Challenge created! Invite your community. 🎉');
                setActiveTab('browse');
                loadChallenges();
              }}
            />
          )}

          {/* Browse / My Challenges */}
          {activeTab !== 'create' && (
            <>
              {/* Category Filter */}
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                <button
                  onClick={() => setFilterCat('all')}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-600 border transition-colors ${filterCat === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/40'}`}
                >
                  All
                </button>
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setFilterCat(cat.value)}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 border transition-colors ${filterCat === cat.value ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-muted-foreground hover:border-primary/40'}`}
                  >
                    {cat.emoji} {cat.label}
                  </button>
                ))}
              </div>

              {/* Grid */}
              {loading ? (
                <div className="flex justify-center py-16">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : displayedChallenges.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <span className="text-5xl mb-4">🏆</span>
                  <p className="font-600 text-foreground">
                    {activeTab === 'my-challenges' ? 'You have not joined any challenges yet' : 'No challenges found'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {activeTab === 'my-challenges' ?'Browse challenges and join one to get started' :'Be the first to create a challenge for the community'}
                  </p>
                  <button
                    onClick={() => setActiveTab(activeTab === 'my-challenges' ? 'browse' : 'create')}
                    className="mt-4 px-4 py-2 bg-primary text-primary-foreground text-sm font-600 rounded-xl hover:bg-primary/90 transition-colors"
                  >
                    {activeTab === 'my-challenges' ? 'Browse Challenges' : 'Create Challenge'}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayedChallenges.map((challenge) => (
                    <ChallengeCard
                      key={challenge.id}
                      challenge={challenge}
                      onJoin={handleJoin}
                      onLog={handleLog}
                      onLeaderboard={setLeaderboardChallenge}
                      joining={joining}
                      logging={logging}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Leaderboard Modal */}
      {leaderboardChallenge && (
        <LeaderboardModal
          challenge={leaderboardChallenge}
          onClose={() => setLeaderboardChallenge(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-foreground text-background text-sm font-500 px-5 py-3 rounded-2xl shadow-xl z-50 animate-fade-in">
          {toast}
        </div>
      )}
    </AppLayout>
  );
}
